import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { invalidationIntents, queryKeys } from "~/api/cache"

import type { SolarProposalAuroraDto } from "../dto/solar-proposal-aurora.dto"
import { solarProposalAuroraService } from "../services/solar-proposal-aurora.service"

export type LinkSolarAuroraDesign = { proposalId: string; ref: string }

/**
 * An administrator links the proposal's first Aurora design (the Source
 * Lock). It runs inline at the gateway and answers the Aurora read; on
 * success every read the design feeds is invalidated, so the step becomes the
 * Aurora drawer and pricing and savings show out of date. A failure refetches
 * them too, since a lock or another tab's link may be why it failed.
 *
 * `onLinked` runs on the mutation, before the invalidation: the refetched
 * design read replaces the form that started the link, so a per-call callback
 * could miss it.
 */
export default function useLinkSolarAuroraDesign({
  onLinked,
}: {
  onLinked?: (saved: SolarProposalAuroraDto) => void
} = {}): UseMutationResult<
  SolarProposalAuroraDto,
  unknown,
  LinkSolarAuroraDesign
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, ref }: LinkSolarAuroraDesign) =>
      solarProposalAuroraService.postLink(proposalId, ref),
    onSuccess: (saved, { proposalId }) => {
      onLinked?.(saved)
      // The drawer reads it at once, so it never shows the not-linked notice.
      queryClient.setQueryData(queryKeys.solarProposal.aurora(proposalId), saved)
      return invalidationIntents.solarAuroraDesignChanged(
        queryClient,
        proposalId,
      )
    },
    onError: (_error, { proposalId }) =>
      invalidationIntents.solarAuroraDesignChanged(queryClient, proposalId),
  })
}
